import React, { useState, useEffect, useContext } from "react";
import { Plus, Trash2, Eye, ChevronDown, Download, X, CheckCircle, User, Building, Mail, Phone, Calendar, Shield, FileText, Users, Database, ShieldAlert, Pencil } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { NotificationContext } from "../../context/NotificationContext";
import authAxios from "../../api/authAxios";
import { businessTypePublicAPI } from "../../api/apiService";
const getDaysRemaining = (expiryDate) => {
  if (!expiryDate) return null;
  const now = new Date();
  const expiry = new Date(expiryDate);
  const days = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
};

const formatErrorData = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.join(", ");
  if (typeof data === "object") {
    return Object.entries(data)
      .map(([key, val]) => {
        const formattedVal = typeof val === "object" ? formatErrorData(val) : val;
        return `${key}: ${formattedVal}`;
      })
      .join(" | ");
  }
  return String(data);
};

export default function UserManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [ownerDetails, setOwnerDetails] = useState({ invoices: [], products: [], customers: [] });
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("invoices");
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [showEditOtherInput, setShowEditOtherInput] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: "",
    business_name: "",
    first_name: "",
    phone: "",
    email: "",
    business_type: "",
    plan_type: "free_trial",
    staff_limit: "",
    auto_backup: false,
  });

  const handleOpenEditModal = (user) => {
    const isCustomType = user.business_type && !businessTypes.some(t => t.name === user.business_type);
    setEditFormData({
      id: user.id,
      business_name: user.last_name || user.business_name || "",
      first_name: user.first_name || "",
      email: user.email || "",
      business_type: user.business_type || "",
      phone: user.phone?.startsWith('+91') ? user.phone.slice(-10) : (user.phone?.startsWith('+') ? user.phone.slice(-10) : (user.phone || "")),
      plan_type: user.subscription?.plan_type || "free_trial",
      staff_limit: user.staff_limit !== null ? user.staff_limit : (user.subscription?.plan_type === "free_trial" ? 2 : (user.subscription?.plan_type === "basic" ? 5 : (user.subscription?.plan_type === "standard" ? 15 : 0))),
      auto_backup: !!user.auto_backup,
    });
    setShowEditOtherInput(isCustomType);
    setShowEditModal(true);
  };

  const handleSaveEditBusiness = async () => {
    if (!editFormData.phone || !editFormData.business_name || !editFormData.first_name || !editFormData.business_type) {
      addNotification("Please fill all required fields", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        last_name: editFormData.business_name,
        first_name: editFormData.first_name,
        email: editFormData.email,
        business_type: editFormData.business_type,
        phone: editFormData.phone.startsWith('+91') ? editFormData.phone : `+91${editFormData.phone}`,
        plan_type: editFormData.plan_type,
        staff_limit: editFormData.staff_limit === "unlimited" || editFormData.staff_limit === -1 || editFormData.staff_limit === "" ? -1 : parseInt(editFormData.staff_limit),
        auto_backup: editFormData.auto_backup
      };

      const res = await authAxios.put(`/api/super-admin/owners/${editFormData.id}/`, payload);
      setUsers(users.map(u => u.id === editFormData.id ? res.data : u));
      
      // Update selectedUser if detail drawer is open
      if (selectedUser && selectedUser.id === editFormData.id) {
        setSelectedUser(res.data);
      }
      
      addNotification("Business details updated successfully", "success");
      setShowEditModal(false);
    } catch (error) {
      console.error("Failed to update business:", error);
      let errorMsg = "Error updating business details";
      if (error.response?.data) {
        errorMsg = formatErrorData(error.response.data);
      }
      addNotification(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    phone: "",
    business_name: "",
    first_name: "",
    email: "",
    business_type: "",
    // Company profile fields
    company_code: "",
    tax_id: "",
    registration_number: "",
    street_address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "India",
    password: "",
    country_code: "+91",
  });

  useEffect(() => {
    fetchUsers();
    fetchBusinessTypes();
    
    const intervalId = setInterval(() => {
      fetchUsersSilent();
    }, 10000);
    
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsersSilent = async () => {
    try {
      const res = await authAxios.get('/api/super-admin/owners/?list=true', { timeout: 30000 });
      const userData = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setUsers(userData);
      if (userData.length > 0) {
        sessionStorage.setItem("owners_list_cache", JSON.stringify(userData));
      }
    } catch (error) {
      // silent fail for polling
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const res = await businessTypePublicAPI.getAll();
      // Sort: Alpha but "Other" always last
      const sorted = res.data.sort((a, b) => {
        if (a.name === "Other") return 1;
        if (b.name === "Other") return -1;
        return a.name.localeCompare(b.name);
      });
      setBusinessTypes(sorted);
    } catch (error) {
      console.error("Failed to fetch business types:", error);
    }
  };


  const fetchUsers = async () => {
    try {
      const cached = sessionStorage.getItem("owners_list_cache");
      if (cached) {
        try {
          const cachedData = JSON.parse(cached);
          if (Array.isArray(cachedData) && cachedData.length > 0) {
            setUsers(cachedData);
            setLoading(false);
          }
        } catch (e) {
          sessionStorage.removeItem("owners_list_cache");
        }
      } else {
        setLoading(true);
      }

      const res = await authAxios.get('/api/super-admin/owners/?list=true', { timeout: 30000 });
      setFetchError(false);
      const userData = Array.isArray(res.data) ? res.data : res.data?.results || [];

      setUsers(userData);
      if (userData.length > 0) {
        sessionStorage.setItem("owners_list_cache", JSON.stringify(userData));
      }

      // Auto-open owner details drawer if ownerId is specified in URL (e.g. from Leads page)
      const targetOwnerId = searchParams.get("ownerId");
      if (targetOwnerId) {
        const matchedUser = userData.find(u => String(u.id) === String(targetOwnerId));
        if (matchedUser) {
          setSelectedUser(matchedUser);
          setShowDetailDrawer(true);
          fetchOwnerDetails(matchedUser.id);
        }
      }
    } catch (error) {
      console.error("Error fetching users list:", error);
      // Only show error screen if we don't already have users loaded from cache
      setFetchError((prev) => {
        const cached = sessionStorage.getItem("owners_list_cache");
        if (cached && JSON.parse(cached).length > 0) return false;
        return true;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!formData.phone || !formData.business_name || !formData.first_name || !formData.business_type) {
      addNotification("Please fill all required fields", "error");
      return;
    }
    
    if (!editingUser && !formData.password) {
      addNotification("Password is required for new owners", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        phone: `${formData.country_code}${formData.phone}`,
        last_name: formData.business_name,
        first_name: formData.first_name,
        business_type: formData.business_type,
        company_profile: {
          tax_id: formData.gstin || "",
          state: formData.state || ""
        }
      };

      if (formData.email) {
        payload.email = formData.email;
      }

      if (formData.password) {
        payload.password = formData.password;
      }

      const res = await authAxios.post('/api/super-admin/owners/', payload);
      setUsers([...users, res.data]);
      addNotification("Owner and company profile created successfully", "success");
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error("DEBUG: handleAddUser error", error.response?.data);
      let errorMsg = "Error creating owner";
      if (error.response?.data) {
        errorMsg = formatErrorData(error.response.data);
      }
      addNotification(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDelete = async (user) => {
    const confirmation = window.prompt(`CRITICAL WARNING: This will permanently delete all data for ${user.first_name} and their business. A final backup will be taken automatically before deletion.\n\nType "DELETE" to confirm permanent removal:`);
    
    if (confirmation !== "DELETE") {
      if (confirmation !== null) addNotification("Confirmation failed. Deletion aborted.", "error");
      return;
    }

    try {
      setLoading(true);
      addNotification("Initiating final backup and permanent deletion...", "info");
      await authAxios.delete(`/api/backup/customer-full-delete/${user.id}/`);
      setUsers(users.filter(u => u.id !== user.id));
      addNotification("Customer and all data permanently deleted", "success");
      setShowDetailDrawer(false);
    } catch (error) {
      addNotification(error.response?.data?.error || "Error during permanent deletion", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      setLoading(true);
      const res = await authAxios.patch(`/api/super-admin/owners/${user.id}/`, {
        is_active: !user.is_active
      });
      setUsers(users.map(u =>
        u.id === user.id ? res.data : u
      ));
      // Update the detail drawer with new data if it's open
      if (selectedUser && selectedUser.id === user.id) {
        setSelectedUser(res.data);
      }
      addNotification(
        user.is_active ? "User deactivated" : "User activated",
        "success"
      );
    } catch (error) {

      addNotification("Error updating user status", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      phone: "",
      business_name: "",
      first_name: "",
      email: "",
      business_type: "",
      company_code: "",
      tax_id: "",
      gstin: "",
      state: "",
      registration_number: "",
      street_address: "",
      city: "",
      postal_code: "",
      country: "India",
      password: "",
      country_code: "+91",
    });
    setShowOtherInput(false);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    const isCustomType = user.business_type && !businessTypes.some(t => t.name === user.business_type);
    setFormData({
      business_name: user.last_name || user.business_name || "",
      first_name: user.first_name || "",
      email: user.email || "",
      business_type: user.business_type || "",
      country_code: user.phone?.startsWith('+') ? user.phone.substring(0, user.phone.length - 10) : "+91",
      phone: user.phone?.startsWith('+') ? user.phone.slice(-10) : (user.phone || ""),
      gstin: "", // Editing owner's company profile is not implemented in this modal currently, it just updates User
      state: ""
    });
    setShowOtherInput(isCustomType);
    setShowModal(true);
    setShowDetailDrawer(false);
  };

  const fetchOwnerDetails = async (userId) => {
    try {
      setLoadingDetails(true);
      const res = await authAxios.get(`/api/super-admin/owners/${userId}/owner_details/`);
      setOwnerDetails(res.data);
    } catch (error) {
      addNotification("Error loading owner details", "error");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!formData.phone || !formData.business_name || !formData.first_name) {
      addNotification("Please fill all required fields", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        phone: `${formData.country_code}${formData.phone}`,
        last_name: formData.business_name,
        first_name: formData.first_name,
        is_active: editingUser.is_active,
      };

      if (formData.business_type) {
        payload.business_type = formData.business_type;
      }

      if (formData.email) {
        payload.email = formData.email;
      }

      const res = await authAxios.patch(`/api/super-admin/owners/${editingUser.id}/`, payload);
      setUsers(users.map(u => u.id === editingUser.id ? res.data : u));
      // Update the detail drawer with new data if it's open
      if (selectedUser && selectedUser.id === editingUser.id) {
        setSelectedUser(res.data);
      }
      addNotification("User updated successfully", "success");
      resetForm();
      setShowModal(false);
    } catch (error) {

      addNotification(error.response?.data?.message || "Error updating user", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;

    // Normalize search term and phone numbers for comparison
    const cleanSearch = searchTerm.replace(/\D/g, "");
    const cleanPhone = (user.phone || "").replace(/\D/g, "");
    const lowerSearch = searchTerm.toLowerCase();

    return (
      (cleanSearch && cleanPhone.includes(cleanSearch)) ||
      (user.phone || "").includes(searchTerm) ||
      (user.last_name || "").toLowerCase().includes(lowerSearch) ||
      (user.business_name || "").toLowerCase().includes(lowerSearch) ||
      (user.first_name || "").toLowerCase().includes(lowerSearch) ||
      (user.email || "").toLowerCase().includes(lowerSearch)
    );
  });

  const applyFilters = (userList) => {
    let filtered = userList;

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(user => {
        const isExpired = user.subscription?.status === "EXPIRED" ||
          (user.subscription?.expiry_date && new Date(user.subscription.expiry_date) < new Date());
        const daysLeft = getDaysRemaining(user.subscription?.expiry_date);
        const isExpiringSoon = daysLeft !== null && daysLeft <= 7 && daysLeft > 0;

        if (statusFilter === "active") return user.is_active && !isExpired;
        if (statusFilter === "inactive") return !user.is_active;
        if (statusFilter === "expired") return isExpired;
        if (statusFilter === "expiring_soon") return isExpiringSoon;
        return true;
      });
    }

    // Plan filter
    if (planFilter !== "all") {
      filtered = filtered.filter(user => {
        const userPlan = user.subscription?.plan_type?.toLowerCase() || "none";
        return userPlan === planFilter.toLowerCase();
      });
    }

    return filtered;
  };

  const finalUsers = applyFilters(filteredUsers);

  const handleExport = () => {
    try {
      const csvData = [
        ["Phone", "Business Name", "Owner Name", "Email", "Status", "Plan", "Date Joined"],
        ...finalUsers.map(user => [
          user.phone,
          user.business_name,
          user.first_name,
          user.email,
          user.is_active ? "Active" : "Inactive",
          user.subscription?.plan_type || "None",
          new Date(user.date_joined).toLocaleDateString()
        ])
      ];

      const csv = csvData.map(row =>
        row.map(cell => `"${cell || ""}"`).join(",")
      ).join("\n");

      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      addNotification("Users exported successfully", "success");
    } catch (error) {

      addNotification("Error exporting users", "error");
    }
  };

  const handleExportData = async (user) => {
    try {
      addNotification("Preparing backup... This may take a moment.", "info");
      const response = await authAxios.get(`/api/super-admin/owners/${user.id}/export_data/`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      let filename = `owner_${user.id}_backup.json`;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(disposition);
          if (matches != null && matches[1]) { 
            filename = matches[1].replace(/['"]/g, '');
          }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      addNotification("Backup downloaded successfully!", "success");
    } catch (error) {
      addNotification("Error downloading backup", "error");
    }
  };

  const handleImpersonate = async (user) => {
    if (!window.confirm(`Are you sure you want to login as ${user.first_name}? Any actions you perform will be recorded under their account.`)) return;
    try {
      const res = await authAxios.post(`/api/super-admin/owners/${user.id}/impersonate/`);
      
      // Save Super Admin session safely
      localStorage.setItem("super_admin_backup_token", localStorage.getItem("token"));
      localStorage.setItem("super_admin_backup_user", localStorage.getItem("user"));
      
      // Override with Owner session
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      
      addNotification(`Logged in as ${user.first_name}. Redirecting...`, "success");
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
      
    } catch (error) {
       addNotification(error.response?.data?.error || "Error during impersonation", "error");
    }
  };

  const handleExtendSubscription = async (userId, days) => {
    try {
      setLoading(true);
      const res = await authAxios.post(`/api/super-admin/owners/${userId}/extend-subscription/`, {
        days: days
      });
      // Update the user in state immediately from response
      if (res.data?.user) {
        setUsers(prev => prev.map(u => u.id === userId ? res.data.user : u));
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(res.data.user);
        }
      } else {
        // Fallback: refetch all users
        fetchUsers();
      }
      addNotification(`Subscription extended by ${days} days`, "success");
    } catch (error) {
      addNotification(error.response?.data?.error || "Error extending subscription", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async (userId, planType) => {
    try {
      setLoading(true);
      await authAxios.post(`/api/super-admin/owners/${userId}/change-plan/`, {
        plan_type: planType
      });
      fetchUsers();
      addNotification(`Plan changed to ${planType}`, "success");
    } catch (error) {

      addNotification("Error changing plan", "error");
    } finally {
      setLoading(false);
    }
  };

  const getPlanLabel = (planType) => {
    if (!planType) return "None";
    return planType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getPlanColor = (planType) => {
    const colors = {
      free_trial: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-200/50 dark:border-indigo-500/20",
      free: "bg-gray-500/20 text-gray-400",
      basic: "bg-blue-500/20 text-blue-400 border border-blue-500/20",
      standard: "bg-purple-500/20 text-purple-400 border border-purple-500/20",
      premium: "bg-emerald-500/20 text-emerald-500 border border-emerald-500/20",
    };
    return colors[planType?.toLowerCase()] || colors.free;
  };

  const getStatusBadge = (user) => {
    const isExpired = user.subscription?.status === "EXPIRED" ||
      (user.subscription?.expiry_date && new Date(user.subscription.expiry_date) < new Date());

    if (!user.is_active) return (
      <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-slate-900/30 text-slate-400 border border-slate-700">
        Suspended
      </span>
    );

    if (isExpired) return (
      <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-red-900/30 text-red-500 border border-red-500/30">
        Expired
      </span>
    );

    if (user.subscription?.plan_type === "free_trial") return (
      <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/30">
        Trial
      </span>
    );

    return (
      <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-emerald-900/30 text-emerald-500 border border-emerald-500/30">
        Active
      </span>
    );
  };



  return (
    <div className="p-6 bg-gray-50 dark:bg-slate-900 min-h-screen text-slate-900 dark:text-slate-100 transition-colors">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-1">User Management</h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium">Managing {finalUsers.length} of {users.length} registered owners</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-bold border border-slate-200 dark:border-slate-700 hover:bg-slate-50 transition-all active:scale-95"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold shadow-lg shadow-slate-900/10 transition-all active:scale-95"
            >
              <Plus className="w-4 h-4" />
              Add New Owner
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mb-6 space-y-4">
          <div className="relative group">
            <input
              type="text"
              placeholder="Search by phone, business name, or owner name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-5 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 transition-all outline-none pl-12 shadow-sm"
            />
            <Eye className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
          </div>

          <div className="flex gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Filter By:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
                <option value="expiring_soon">Expiring Soon</option>
              </select>

              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-sm"
              >
                <option value="all">All Plans</option>
                <option value="free_trial">Free Trial</option>
                <option value="basic">Basic</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-4">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="font-bold uppercase tracking-widest text-xs">Loading Secure Data...</span>
          </div>
        ) : fetchError ? (
          <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-3xl border border-dashed border-red-200 dark:border-red-900/30 transition-colors shadow-sm">
            <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Failed to load users</h3>
            <p className="text-slate-500 text-sm">Please check your connection and try again.</p>
          </div>
        ) : finalUsers.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700 transition-colors shadow-sm">
            <Building className="w-12 h-12 text-slate-200 dark:text-slate-700 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">No users found</h3>
            <p className="text-slate-500 text-sm">Try adjusting your filters or search term</p>
          </div>
        ) : (
          <div className="overflow-hidden bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 transition-colors shadow-xl">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Business Info</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Plan Info</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Usage</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Subscription Status</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Backup Status</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Account</th>
                  <th className="px-6 py-5 text-right text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {finalUsers.map((user) => {
                  const daysRemaining = getDaysRemaining(user.subscription?.expiry_date);
                  const isExpired = user.subscription?.status === "EXPIRED" ||
                    (user.subscription?.expiry_date && new Date(user.subscription.expiry_date) < new Date());
                  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0;

                  return (
                    <tr
                      key={user.id}
                      className={`group hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors ${isExpired ? "bg-red-50/30 dark:bg-red-900/5" : ""}`}
                    >
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-1">
                          <span className="text-base font-black text-slate-900 dark:text-white">
                            {user.last_name || user.business_name || "Unknown Store"}
                          </span>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs font-bold text-slate-500 flex items-center gap-1.5 uppercase tracking-tight">
                              Owner: <span className="text-slate-700 dark:text-slate-300 font-black">{user.first_name}</span>
                            </span>
                            <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5 group/phone">
                              <Phone className="w-3 h-3 text-slate-300 group-hover/phone:text-blue-500 transition-colors" />
                              <span className="font-mono group-hover/phone:text-slate-600 dark:group-hover:text-slate-200 transition-colors">{user.phone}</span>
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-1.5">
                          <div className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider w-fit border ${getPlanColor(user.subscription?.plan_type)}`}>
                            {getPlanLabel(user.subscription?.plan_type)}
                          </div>
                          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-tight">
                            Type: {user.business_type || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-black text-slate-700 dark:text-slate-200">
                            Staff: {user.staff_count || 0} / {user.staff_limit === -1 ? '∞' : (user.staff_limit || 0)}
                          </span>
                          <div className="w-24 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${user.staff_count >= user.staff_limit && user.staff_limit !== -1 ? 'bg-red-500' : 'bg-blue-500'}`}
                              style={{ width: user.staff_limit === -1 ? '10%' : `${Math.min(100, (user.staff_count / user.staff_limit) * 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-1">
                          {user.subscription?.expiry_date ? (
                            <>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-black ${isExpiringSoon ? 'text-indigo-600 dark:text-indigo-400' : isExpired ? 'text-red-500' : 'text-slate-700 dark:text-slate-200'}`}>
                                  {isExpired ? 'Expired' : `Expires in ${daysRemaining} days`}
                                </span>
                                {isExpiringSoon && (
                                  <div className="p-1 bg-indigo-500 text-white rounded-full animate-pulse shadow-lg shadow-indigo-500/30" title="Expiring Soon!">
                                    <Shield className="w-2.5 h-2.5" />
                                  </div>
                                )}
                              </div>
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                {new Date(user.subscription.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                            </>
                          ) : (
                            <span className="text-slate-400 font-bold italic text-sm">No Active Sub</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-1">
                          <span className={`text-xs font-bold uppercase tracking-tight ${user.last_backup_date ? 'text-emerald-500' : 'text-slate-400'}`}>
                            {user.last_backup_date ? 'Secured' : 'No Backups'}
                          </span>
                          <span className="text-[10px] font-medium text-slate-400">
                             {user.last_backup_date ? new Date(user.last_backup_date).toLocaleDateString() : 'Risk of data loss'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        {getStatusBadge(user)}
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex justify-end gap-2 transition-opacity">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDetailDrawer(true);
                              fetchOwnerDetails(user.id);
                              setActiveTab("invoices");
                            }}
                            className="p-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-900 dark:hover:bg-white text-slate-600 dark:text-slate-300 hover:text-white dark:hover:text-slate-900 rounded-xl transition-all active:scale-95"
                            title="View Detail"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenEditModal(user)}
                            className="p-2.5 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-600 text-blue-600 hover:text-white rounded-xl transition-all active:scale-95"
                            title="Edit Business"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              window.location.href = `/super-admin?tab=backup&ownerId=${user.id}`;
                            }}
                            className="p-2.5 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-600 text-indigo-600 hover:text-white rounded-xl transition-all active:scale-95"
                            title="Manage Backup"
                          >
                            <Database className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(user)}
                            disabled={loading}
                            className="p-2.5 bg-red-50 dark:bg-red-900/20 hover:bg-red-600 text-red-500 hover:text-white rounded-xl transition-all active:scale-95 disabled:opacity-50"
                            title="Delete Permanently"
                          >
                            <ShieldAlert className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Drawer */}
        {showDetailDrawer && selectedUser && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-all" onClick={() => setShowDetailDrawer(false)} />
        )}
        {showDetailDrawer && selectedUser && (
          <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-50 overflow-y-auto transition-transform duration-300 ease-out">
            <div className="p-8">
              {/* Header */}
              <div className="flex justify-between items-start mb-8">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                      <Building className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-none mb-2">
                        {selectedUser.last_name || selectedUser.business_name}
                      </h2>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(selectedUser)}
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${getPlanColor(selectedUser.subscription?.plan_type)}`}>
                          {getPlanLabel(selectedUser.subscription?.plan_type)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailDrawer(false)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              {/* Informative Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Owner Name</p>
                  <p className="text-lg font-black text-slate-900 dark:text-white">{selectedUser.first_name}</p>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 group/item">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Contact Phone</p>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-300 group-hover/item:text-blue-500 transition-colors" />
                    <p className="text-lg font-black text-slate-900 dark:text-white font-mono">{selectedUser.phone}</p>
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Email Address</p>
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-300 truncate">{selectedUser.email || "Not Provided"}</p>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Business Category</p>
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase">{selectedUser.business_type || "N/A"}</p>
                </div>
              </div>

              {/* Registration Details */}
              <div className="mb-8 p-6 rounded-3xl bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Registration Details
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-slate-200 dark:border-slate-700/50 pb-2">
                    <span className="text-xs font-bold text-slate-500">Full Name</span>
                    <span className="text-sm font-black text-slate-900 dark:text-white">{selectedUser.original_registration_name || selectedUser.first_name || "N/A"}</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-slate-200 dark:border-slate-700/50 pb-2">
                    <span className="text-xs font-bold text-slate-500">Mobile Number</span>
                    <span className="text-sm font-black text-slate-900 dark:text-white font-mono">{selectedUser.original_registration_phone || selectedUser.phone || "N/A"}</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-slate-200 dark:border-slate-700/50 pb-2">
                    <span className="text-xs font-bold text-slate-500">Registered On</span>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                      {selectedUser.date_joined ? new Date(selectedUser.date_joined).toLocaleString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: 'numeric', minute: '2-digit', hour12: true
                      }) : "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-500">Status</span>
                    <span className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider ${selectedUser.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                      {selectedUser.is_active ? 'Approved' : 'Pending Approval'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Subscription Details */}
              <div className="mb-8 p-6 rounded-3xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xl overflow-hidden relative">
                <div className="absolute right-[-10px] top-[-10px] opacity-10">
                  <Shield className="w-32 h-32" />
                </div>
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Subscription Architecture
                </h3>

                <div className="space-y-6 relative z-10">
                  <div className="flex justify-between items-end border-b border-white/10 dark:border-slate-200 pb-4">
                    <div>
                      <p className="text-[10px] font-black opacity-50 uppercase tracking-widest">Active Plan</p>
                      <p className="text-2xl font-black">{getPlanLabel(selectedUser.subscription?.plan_type)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold opacity-70 mb-1">Auto Renew</p>
                      <span className="px-2 py-0.5 bg-emerald-500 text-white rounded text-[10px] font-black uppercase">Enabled</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-[10px] font-black opacity-50 uppercase tracking-widest mb-1">User Limit</p>
                      <p className="text-xl font-black">{selectedUser.staff_count} / {selectedUser.staff_limit === -1 ? 'Unlimited' : selectedUser.staff_limit}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black opacity-50 uppercase tracking-widest mb-1">Days to Expiry</p>
                      <p className={`text-xl font-black ${getDaysRemaining(selectedUser.subscription?.expiry_date) <= 7 ? 'text-indigo-400' : ''}`}>
                        {getDaysRemaining(selectedUser.subscription?.expiry_date)} Days
                      </p>
                    </div>
                  </div>

                  <div className="pt-2">
                    <p className="text-[10px] font-black opacity-50 uppercase tracking-widest mb-1">Contract End Date</p>
                    <p className="text-base font-bold">
                      {selectedUser.subscription?.expiry_date ? new Date(selectedUser.subscription.expiry_date).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'long', year: 'numeric'
                      }) : "No active contract"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Advanced Actions */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">Management Controls</p>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => handleToggleActive(selectedUser)}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-bold transition-all ${selectedUser.is_active
                      ? "bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-600 hover:text-white"
                      : "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 hover:bg-emerald-600 hover:text-white"
                      }`}
                  >
                    <Shield className="w-4 h-4" />
                    {selectedUser.is_active ? "Suspend User" : "Reactivate User"}
                  </button>
                </div>
                <button
                  onClick={() => {
                    const days = window.prompt("Extend subscription by how many days?", "30");
                    if (days) handleExtendSubscription(selectedUser.id, parseInt(days));
                  }}
                  className="w-full px-4 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg shadow-blue-600/20 transition-all hover:scale-[1.02] active:scale-95 mt-3"
                >
                  Quick Extend Subscription
                </button>
              </div>
              {/* Master Detailing Controls */}
              <div className="mt-10 border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Master Ledger & Analytics</h3>
                
                <div className="grid grid-cols-1 gap-3">
                  <button 
                    onClick={() => navigate(`/super-admin/owner/${selectedUser.id}/invoices`)}
                    className="flex justify-between items-center p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl hover:border-blue-500 hover:shadow-lg transition-all group"
                  >
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-black text-slate-900 dark:text-white group-hover:text-blue-600 transition-colors">Invoice History</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase">View & Export Master Sales</p>
                        </div>
                    </div>
                    <ChevronDown className="w-5 h-5 text-slate-300 -rotate-90 group-hover:text-blue-500 transition-all" />
                  </button>

                  <button 
                    onClick={() => navigate(`/super-admin/owner/${selectedUser.id}/inventory`)}
                    className="flex justify-between items-center p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl hover:border-emerald-500 hover:shadow-lg transition-all group"
                  >
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                            <Building className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-black text-slate-900 dark:text-white group-hover:text-emerald-600 transition-colors">Inventory Master</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase">Products, Suppliers & Stock</p>
                        </div>
                    </div>
                    <ChevronDown className="w-5 h-5 text-slate-300 -rotate-90 group-hover:text-emerald-500 transition-all" />
                  </button>

                  <button 
                    onClick={() => navigate(`/super-admin/owner/${selectedUser.id}/customers`)}
                    className="flex justify-between items-center p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl hover:border-indigo-500 hover:shadow-lg transition-all group"
                  >
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                            <Users className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-black text-slate-900 dark:text-white group-hover:text-indigo-600 transition-colors">Customer Base</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase">Registered Customer Directory</p>
                        </div>
                    </div>
                    <ChevronDown className="w-5 h-5 text-slate-300 -rotate-90 group-hover:text-indigo-500 transition-all" />
                  </button>

                  <button 
                    onClick={() => navigate(`/super-admin/owner/${selectedUser.id}/employees`)}
                    className="flex justify-between items-center p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl hover:border-orange-500 hover:shadow-lg transition-all group"
                  >
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-orange-500/10 text-orange-600 flex items-center justify-center group-hover:bg-orange-600 group-hover:text-white transition-colors">
                            <Shield className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-black text-slate-900 dark:text-white group-hover:text-orange-600 transition-colors">Team Management</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase">View Employees & Sales Staff</p>
                        </div>
                    </div>
                    <ChevronDown className="w-5 h-5 text-slate-300 -rotate-90 group-hover:text-orange-500 transition-all" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit User Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-full max-w-2xl border border-slate-200 dark:border-slate-700 my-8 transition-colors shadow-2xl">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                {editingUser ? "Edit Owner" : "Create New Owner"}
              </h2>

              <div className="space-y-6 max-h-[80vh] overflow-y-auto pr-2 custom-scrollbar transition-colors">
                {/* Owner Info */}
                <div>
                  <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4">Owner Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Phone *
                      </label>
                      <div className="flex gap-0 group/phone">
                        <div className="flex items-center px-4 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-l-xl text-slate-500 font-bold border-r-0">
                          {formData.country_code}
                        </div>
                        <input
                          type="text"
                          value={formData.phone}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            if (val.length <= 10) {
                              setFormData({ ...formData, phone: val });
                            }
                          }}
                          className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-r-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none"
                          placeholder="10 digit mobile number"
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1 ml-1 uppercase font-bold tracking-tight">Exactly 10 digits required</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        value={formData.business_name}
                        onChange={(e) =>
                          setFormData({ ...formData, business_name: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none"
                        placeholder="Enter business name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Owner Name *
                      </label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) =>
                          setFormData({ ...formData, first_name: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none"
                        placeholder="Enter owner name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none"
                        placeholder="Enter email (optional)"
                      />
                    </div>

                    {!editingUser && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                          Password *
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) =>
                            setFormData({ ...formData, password: e.target.value })
                          }
                          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none"
                          placeholder="Enter account password"
                          required={!editingUser}
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Business Type *
                      </label>
                      <select
                        value={showOtherInput ? "Other" : formData.business_type}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "Other") {
                            setShowOtherInput(true);
                            setFormData({ ...formData, business_type: "" });
                          } else {
                            setShowOtherInput(false);
                            setFormData({ ...formData, business_type: val });
                          }
                        }}
                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none"
                      >
                        <option value="">Select Business Type</option>
                        {businessTypes.map((type) => (
                          type.name !== "Other" && (
                            <option key={type.id} value={type.name}>
                              {type.name}
                            </option>
                          )
                        ))}
                        <option value="Other">Other</option>
                      </select>

                      {showOtherInput && (
                        <div className="mt-3 animate-in fade-in slide-in-from-top-1 duration-200">
                          <label 
                            className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                            style={{ fontFamily: "'Lexend', sans-serif" }}
                          >
                            Specify Business Type *
                          </label>

                          <input
                            type="text"
                            value={formData.business_type}
                            onChange={(e) =>
                              setFormData({ ...formData, business_type: e.target.value })
                            }
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none italic"
                            style={{ fontFamily: "'Lexend', sans-serif" }}
                            placeholder="e.g. Mobile Shop, Cafe, etc."
                          />
                        </div>
                      )}


                    </div>

                    {!editingUser && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                            GSTIN (Tax ID)
                          </label>
                          <input
                            type="text"
                            value={formData.gstin}
                            onChange={(e) =>
                              setFormData({ ...formData, gstin: e.target.value })
                            }
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none"
                            placeholder="e.g. 29ABCDE1234F1Z5"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                            State
                          </label>
                          <input
                            type="text"
                            value={formData.state}
                            onChange={(e) =>
                              setFormData({ ...formData, state: e.target.value })
                            }
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none"
                            placeholder="e.g. Karnataka"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    resetForm();
                    setShowModal(false);
                  }}
                  className="flex-1 px-4 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-white rounded-xl font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={editingUser ? handleUpdateUser : handleAddUser}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg font-medium transition"
                >
                  {loading ? "Saving..." : editingUser ? "Update" : "Create Owner"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Business Details Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-2xl border border-slate-100 dark:border-slate-700 shadow-2xl transition-all scale-in duration-300 flex flex-col max-h-[90vh]">
              
              {/* Modal Header */}
              <div className="flex justify-between items-center px-8 py-6 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                    <Pencil className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">Edit Business Details</h2>
                    <p className="text-xs text-slate-400 font-medium">Modify administrative, usage, and subscription properties</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-xl text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6 custom-scrollbar">
                
                {/* Section: Core Business details */}
                <div>
                  <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4">Core Identity</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        value={editFormData.business_name}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, business_name: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none font-medium text-sm"
                        placeholder="Enter business name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                        Owner Name *
                      </label>
                      <input
                        type="text"
                        value={editFormData.first_name}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, first_name: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none font-medium text-sm"
                        placeholder="Enter owner name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                        Contact Number *
                      </label>
                      <div className="flex rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
                        <span className="inline-flex items-center px-3.5 bg-slate-100 dark:bg-slate-900 text-slate-500 font-mono text-xs border-r border-slate-200 dark:border-slate-700">
                          +91
                        </span>
                        <input
                          type="text"
                          value={editFormData.phone}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            if (val.length <= 10) {
                              setEditFormData({ ...editFormData, phone: val });
                            }
                          }}
                          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 transition-all focus:outline-none font-mono text-sm"
                          placeholder="10 digit contact number"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                        Business Category/Type *
                      </label>
                      <select
                        value={showEditOtherInput ? "Other" : editFormData.business_type}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "Other") {
                            setShowEditOtherInput(true);
                            setEditFormData({ ...editFormData, business_type: "" });
                          } else {
                            setShowEditOtherInput(false);
                            setEditFormData({ ...editFormData, business_type: val });
                          }
                        }}
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none font-medium text-sm"
                        required
                      >
                        <option value="">Select Business Type</option>
                        {businessTypes.map((type) => (
                          type.name !== "Other" && (
                            <option key={type.id} value={type.name}>
                              {type.name}
                            </option>
                          )
                        ))}
                        <option value="Other">Other</option>
                      </select>

                      {showEditOtherInput && (
                        <div className="mt-3 animate-in fade-in slide-in-from-top-1 duration-200">
                          <label 
                            className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2"
                          >
                            Specify Business Type *
                          </label>
                          <input
                            type="text"
                            value={editFormData.business_type}
                            onChange={(e) =>
                              setEditFormData({ ...editFormData, business_type: e.target.value })
                            }
                            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none font-medium text-sm"
                            placeholder="e.g. Mobile Shop, Cafe, etc."
                            required
                          />
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, email: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none font-medium text-sm"
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>
                </div>

                {/* Section: Subscription and Usage */}
                <div className="border-t border-slate-100 dark:border-slate-700 pt-6">
                  <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4">Subscription & Usage Rules</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                        Subscription Plan *
                      </label>
                      <select
                        value={editFormData.plan_type}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, plan_type: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none font-medium text-sm"
                        required
                      >
                        <option value="free_trial">Free Trial</option>
                        <option value="free">Free</option>
                        <option value="basic">Basic</option>
                        <option value="standard">Standard</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 flex justify-between">
                        <span>Staff Limit override</span>
                        <span className="text-[10px] text-slate-400 font-normal lowercase font-bold tracking-tight">Enter -1 for unlimited</span>
                      </label>
                      <input
                        type="number"
                        value={editFormData.staff_limit}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, staff_limit: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all focus:outline-none font-medium text-sm font-mono"
                        placeholder="e.g. 5, 15, or -1"
                      />
                    </div>
                  </div>
                </div>

                {/* Section: Data & Backup */}
                <div className="border-t border-slate-100 dark:border-slate-700 pt-6">
                  <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4">SaaS Integrations</h3>
                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white">Auto Backup Status</h4>
                      <p className="text-xs text-slate-400 font-medium">Automatically back up complete business data securely to cloud server</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editFormData.auto_backup}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, auto_backup: e.target.checked })
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none dark:bg-slate-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

              </div>

              {/* Modal Footer */}
              <div className="px-8 py-5 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30 flex gap-3 justify-end rounded-b-3xl">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-5 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEditBusiness}
                  disabled={loading}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-600/20 transition-all flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : "Save Changes"}
                </button>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}
